/**
 * Odvozené výpočty fáze 4 (formulář NAV + náhled přepočtu) nad jádrem fundCalc.
 * Vstupy/výstupy jsou `number` (kontrakt mock vrstvy), uvnitř se počítá v Dec —
 * float se nikdy nesčítá ani nedělí přímo.
 */

import { Dec, DecInput } from './dec'
import { MONEY_SCALE, UNIT_SCALE, roundMoney, roundUnits, unitPrice, unitsFor } from './fundCalc'

/** Součet NAV položek (scale 4, HALF_UP). */
export function navTotalOf(items: Array<{ amount: number }>): number {
  return items
    .reduce<Dec>((acc, item) => acc.add(Dec.of(item.amount)), Dec.ZERO)
    .setScale(MONEY_SCALE)
    .toNumber()
}

/** Cena jednotky ocenění = NAV / total_units (fallback při total <= 0). */
export function valuationUnitPrice(navTotal: number, totalUnits: number, fallback: number): number {
  return unitPrice(navTotal, totalUnits, fallback).toNumber()
}

/**
 * Relativní odchylka nového NAV proti minulému (|nové − minulé| / minulé).
 * Vrací null, když minulé ocenění není nebo je nulové — pak není s čím srovnávat.
 */
export function navDeviation(navTotal: number, previousNav: number | undefined): number | null {
  if (previousNav === undefined || previousNav === 0) {
    return null
  }
  const previous = Dec.of(previousNav)
  return Dec.of(navTotal).sub(previous).abs().div(previous.abs(), 6).toNumber()
}

/** Dopad změny celkového počtu jednotek na cenu jednotky (podklad pro ruční opravu). */
export interface TotalUnitsImpact {
  /** Cena ze stejného NAV při současném / novém počtu jednotek (0 = nelze spočítat). */
  priceBefore: number
  priceAfter: number
  /** Relativní změna ceny ((po − před) / před); null, když se cena spočítat nedá. */
  priceChange: number | null
  /** Poměr nový/současný počet jednotek — odhalí řádový překlep; null při nulovém stavu. */
  unitsRatio: number | null
}

/**
 * Co ruční oprava total_units udělá s cenou jednotky: obě ceny se počítají ze STEJNÉHO
 * NAV (posledního ocenění), takže rozdíl je čistě dopadem změny jmenovatele. Celé v Dec
 * (HALF_UP, 8 des. míst jako BE) — nikdy ve float.
 */
export function totalUnitsImpact(
  navTotal: number,
  totalUnitsBefore: number,
  totalUnitsAfter: DecInput
): TotalUnitsImpact {
  const before = Dec.of(totalUnitsBefore)
  const after = Dec.of(totalUnitsAfter)
  // fallback 0 = „cena se z tohohle počtu jednotek spočítat nedá" (nulový/záporný stav)
  const priceBefore = unitPrice(navTotal, before, Dec.ZERO)
  const priceAfter = unitPrice(navTotal, after, Dec.ZERO)
  const priceChange =
    priceBefore.signum() > 0 ? priceAfter.sub(priceBefore).div(priceBefore, 6).toNumber() : null
  const unitsRatio = before.signum() > 0 ? after.div(before, 6).toNumber() : null
  return {
    priceBefore: priceBefore.toNumber(),
    priceAfter: priceAfter.toNumber(),
    priceChange,
    unitsRatio,
  }
}

export interface PreviewParticipantInput {
  depositId: string
  clientId: string
  clientName: string
  /** Vklad pro výpočet jednotek = amountReceived ?? amount (rozhodnutí F3-B). */
  amount: number
  priceOverride?: number
  unitsOverride?: number
}

export interface PreviewParticipant extends PreviewParticipantInput {
  /** Cena použitá pro řádek (priceOverride, jinak cena ocenění). */
  price: number
  /** Přidělené jednotky (unitsOverride, jinak vklad/cena). */
  units: number
}

export interface BatchPreview {
  unitPrice: number
  totalUnitsBefore: number
  /** Σ přidělených jednotek účastníků. */
  unitsAssigned: number
  /** Kumulativní total po přepočtu = před + Σ (nikdy klouzavé okno — SEMI bug). */
  totalUnitsAfter: number
  participants: PreviewParticipant[]
}

/** Náhled přepočtu: cena z ocenění, jednotky per účastník vč. overridů, total po. */
export function computeBatchPreview(input: {
  totalUnitsBefore: number
  unitPrice: number
  participants: PreviewParticipantInput[]
}): BatchPreview {
  const price = Dec.of(input.unitPrice)
  const before = roundUnits(input.totalUnitsBefore)
  let assigned = Dec.ZERO.setScale(UNIT_SCALE)

  const participants = input.participants.map((participant) => {
    const rowPrice = participant.priceOverride != null ? Dec.of(participant.priceOverride) : price
    const units =
      participant.unitsOverride != null
        ? roundUnits(participant.unitsOverride)
        : unitsFor(participant.amount, rowPrice)
    assigned = roundUnits(assigned.add(units))
    return { ...participant, price: rowPrice.toNumber(), units: units.toNumber() }
  })

  return {
    unitPrice: price.toNumber(),
    totalUnitsBefore: before.toNumber(),
    unitsAssigned: assigned.toNumber(),
    totalUnitsAfter: roundUnits(before.add(assigned)).toNumber(),
    participants,
  }
}

/** Kontrola „cena × jednotky ≈ vklad" (na 4 des. místa) — pro náhled i budoucí F5 testy. */
export function reconstructedAmount(units: number, price: number): number {
  return roundMoney(Dec.of(units).mul(Dec.of(price))).toNumber()
}
